import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Plus, Pencil, Search } from "lucide-react";
import ReactECharts from 'echarts-for-react';
import {
  backendGroupAndRhesusToGroupLabel,
  createBanqueSang,
  deleteBanqueSang,
  demandSang,
  getBanquesSang,
  getStocks,
  groupLabelToBackendGroupAndRhesus,
  updateBanqueSang,
  updateStocks,
} from "../../../api/etablissement";
import { useAuth } from "../../../store/authEtablissementStore";

type Group = { key: string; label: string; qty: number };
type DemandeUrgence = "faible" | "moyenne" | "haute" | "critique";

type Bag = {
  pocketId?: string;
  sac: string;
  rhesus: "Rh+" | "Rh-";
  volume: number; // ml
  collecte: string;
  peremption: string;
  temperature: number; // °C
  localisation: string;
  lot: string;
};

function toRhLabel(rhesus: "positif" | "negatif"): Bag["rhesus"] {
  return rhesus === "positif" ? "Rh+" : "Rh-";
}

function formatDateFR(input?: string | Date): string {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  // Backend envoie souvent ISO ; on affiche dd/mm/yyyy
  return d.toLocaleDateString("fr-FR");
}

export default function BloodPage() {
  const { establishment } = useAuth();
  const [query, setQuery] = useState("");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banqueId, setBanqueId] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [bagsMap, setBagsMap] = useState<Record<string, Bag[]>>({});
  const [saving, setSaving] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroup, setNewGroup] = useState<Group>({ key: "", label: "", qty: 0 });
  const [demandOpen, setDemandOpen] = useState(false);
  const [demandForm, setDemandForm] = useState<{
    quantite: number;
    urgence: DemandeUrgence;
    motif: string;
  }>({ quantite: 450, urgence: 'faible', motif: "" });
  const chartRef = useRef<any>(null);
  const filteredGroups = useMemo(() => groups.filter((g) => g.label.toLowerCase().includes(query.toLowerCase())), [groups, query]);

  const loadStocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    const banquesRes = await getBanquesSang();
    if (!banquesRes.ok) {
      setError(banquesRes.message || "Impossible de charger les banques de sang.");
      setLoading(false);
      return;
    }
    const banques = banquesRes.data;
    const firstId = banques?.[0]?.id;
    if (!firstId) {
      setGroups([]);
      setBanqueId(null);
      setLoading(false);
      return;
    }
    setBanqueId(firstId);
    const stocksRes = await getStocks(firstId);
    if (!stocksRes.ok) {
      setError(stocksRes.message || "Impossible de charger les stocks.");
      setLoading(false);
      return;
    }
    const pockets = stocksRes.data;

    const byGroup = new Map<string, typeof pockets>();
    for (const pocket of pockets) {
      const label = backendGroupAndRhesusToGroupLabel(pocket.groupeSanguin, pocket.rhesus);
      if (!byGroup.has(label)) byGroup.set(label, []);
      byGroup.get(label)!.push(pocket);
    }

    const nextGroups: Group[] = Array.from(byGroup.entries()).map(([label, ps]) => {
      const qty = ps.reduce((sum, p) => sum + (Number(p.volume) || 0), 0);
      return { key: label, label, qty };
    });

    const nextBagsMap: Record<string, Bag[]> = {};
    for (const [label, ps] of byGroup.entries()) {
      nextBagsMap[label] = ps.map((p, i) => ({
        pocketId: p.id,
        sac: String(i + 1).padStart(2, "0"),
        rhesus: toRhLabel(p.rhesus),
        volume: Number(p.volume) || 0,
        collecte: formatDateFR(p.dateCollecte),
        peremption: formatDateFR(p.datePeremption),
        temperature: Number(p.temperature) || 0,
        localisation: p.localisation ?? "",
        lot: p.lot ?? "",
      }));
    }

    setGroups(nextGroups);
    setBagsMap(nextBagsMap);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadStocks();
  }, [loadStocks]);

  // Gestion du resize pour le graphique ECharts
  useEffect(() => {
    const handleResize = () => {
      chartRef.current?.getEchartsInstance()?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Configuration du graphique ECharts
  const getChartOption = () => {
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => {
          const param = params[0];
          return `${param.name}<br/>Stock disponible: ${param.value}`;
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#00553E',
        borderWidth: 1,
        textStyle: {
          color: '#fff'
        }
      },
      grid: {
        left: '60px',
        right: '20px',
        top: '20px',
        bottom: '40px',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: filteredGroups.map(g => g.label),
        axisLabel: {
          color: '#666',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: '#E5E7EB'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Quantité',
        nameTextStyle: {
          color: '#666',
          fontSize: 12
        },
        axisLabel: {
          color: '#666',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: '#E5E7EB'
          }
        },
        splitLine: {
          lineStyle: {
            color: '#E5E7EB',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Stock disponible',
          type: 'bar',
          data: filteredGroups.map(g => g.qty),
          itemStyle: {
            color: '#00553E',
            borderRadius: [10, 10, 6, 6]
          },
          label: {
            show: true,
            position: 'top',
            color: '#0B1F16',
            fontSize: 12,
            fontWeight: 'medium'
          },
          emphasis: {
            itemStyle: {
              color: '#004232'
            }
          }
        }
      ],
      animation: true,
      animationDuration: 1000
    };
  };

  const addGroup = async () => {
    if (!newGroup.label.trim()) return;
    if (!banqueId && !establishment) return;

    const label = newGroup.label.toUpperCase().trim();
    const parsed = groupLabelToBackendGroupAndRhesus(label);
    if (!parsed) {
      setError("Libellé de groupe invalide (ex: AB+ ou O-).");
      return;
    }

    if (!establishment?.email) {
      setError("Créateur (email établissement) introuvable.");
      return;
    }

    const now = new Date();
    const dateCollecte = now.toISOString();
    const datePeremption = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000).toISOString();
    const payload = {
      ...parsed,
      volume: Number(newGroup.qty) || 0,
      dateCollecte,
      datePeremption,
      lot: `LOT_${label}_${Date.now()}`,
      temperature: 0,
      localisation: "",
      createur: establishment.email,
    };

    setSaving(true);
    const res = await createBanqueSang(payload);
    setSaving(false);

    if (!res.ok) {
      setError(res.message || "Erreur lors de la création de la poche.");
      return;
    }

    setAddingGroup(false);
    setNewGroup({ key: "", label: "", qty: 0 });
    await loadStocks();
  };
  const removeGroup = async (key: string) => {
    try {
      const bags = bagsMap[key] || [];
      const pocketIds = bags.map((b) => b.pocketId).filter((x): x is string => Boolean(x));

      if (pocketIds.length === 0) {
        setGroups((prev) => prev.filter((g) => g.key !== key));
        setBagsMap((prev) => {
          const p = { ...prev };
          delete p[key];
          return p;
        });
        if (openKey === key) setOpenKey(null);
        return;
      }

      setSaving(true);
      for (const pocketId of pocketIds) {
        const res = await deleteBanqueSang(pocketId);
        if (!res.ok) throw new Error(res.message);
      }
      setSaving(false);

      if (openKey === key) setOpenKey(null);
      await loadStocks();
    } catch (e: any) {
      setSaving(false);
      setError(e?.message || "Erreur lors de la suppression du groupe.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Gestion des sangs</h1>
        <div className="py-12 text-center text-gray-600">Chargement des stocks…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Gestion des sangs</h1>
        <div className="rounded-[10px] border p-6 text-center" style={{ borderColor: "#E5E7EB" }}>
          <p className="text-gray-700">{error}</p>
          <button
            type="button"
            onClick={() => loadStocks()}
            className="mt-4 h-10 px-4 rounded-[10px] font-medium"
            style={{ backgroundColor: "#00553E", color: "#fff" }}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold">Gestion des sangs</h1>

      <div className="flex items-center gap-3">
        <input
          placeholder="Rechercher un groupe sanguin"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 h-11 rounded-[10px] border-2 px-3 bg-white"
          style={{ borderColor: "#D1E2DD" }}
        />
        <button
          type="button"
          aria-label="Rechercher"
          className="h-11 px-4 rounded-[10px] font-medium flex items-center justify-center border-2 border-transparent transition-all duration-200 cursor-pointer bg-[#00553E] text-white hover:bg-white hover:text-[#00553E] hover:border-[#00553E] hover:scale-[1.02] active:scale-[0.98]"
        >
          <Search size={18} className="shrink-0" aria-hidden />
        </button>
      </div>

      <div className="bg-white rounded-[10px] p-6 border overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
        {filteredGroups.length === 0 ? (
          <div className="h-[400px] flex items-center justify-center text-gray-500">
            Aucun résultat
          </div>
        ) : (
          <ReactECharts
            ref={chartRef}
            option={getChartOption()}
            style={{ height: '400px', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <div />
        <div className="flex gap-3">
          {edit && (
            <button
              onClick={() => setAddingGroup(true)}
              className="h-10 px-4 rounded-[10px] border bg-white inline-flex items-center gap-2"
              style={{ borderColor: "#D1E2DD", color: "#00553E" }}
            >
              <Plus size={16} /> Ajouter un groupe
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              if (edit && banqueId) {
                const summary: Record<string, number> = Object.fromEntries(
                  Object.entries(bagsMap).map(([label, bags]) => [
                    label,
                    (bags || []).reduce((sum, b) => sum + (Number(b.volume) || 0), 0),
                  ])
                );
                setSaving(true);
                const res = await updateStocks(banqueId, summary);
                setSaving(false);
                if (!res.ok) {
                  setError(res.message || "Erreur lors de l'enregistrement.");
                  return;
                }
              }
              setEdit((e) => !e);
            }}
            disabled={edit && (!banqueId || saving)}
            className={`h-10 px-4 rounded-[10px] font-medium text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 border-2 border-transparent transition-all duration-200 bg-[#00553E] text-white hover:bg-white hover:text-[#00553E] hover:border-[#00553E] ${
              edit && (!banqueId || saving)
                ? "opacity-60 pointer-events-none"
                : "cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
            }`}
          >
            {edit ? (
              saving ? (
                "Enregistrement…"
              ) : (
                "Enregistrer"
              )
            ) : (
              <>
                <Pencil size={16} className="shrink-0" aria-hidden />
                Mise à jour des stocks
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[10px] border" style={{ borderColor: "#E5E7EB" }}>
        <div className="hidden md:grid md:grid-cols-12 gap-x-6 px-6 py-4 font-semibold text-gray-700">
          <div className="md:col-span-6">Groupe sanguin</div>
          <div className="md:col-span-2">Quantité</div>
          <div className="md:col-span-4 text-right">Informations</div>
        </div>
        <div className="px-4 pb-4 space-y-3">
          {filteredGroups.length === 0 ? (
            <div className="text-gray-500 px-3">Aucun résultat</div>
          ) : (
            filteredGroups.map((g) => (
              <div
                key={g.key}
                className="bg-gray-100 rounded-[10px] p-3 md:px-4 md:py-2 md:h-12 flex flex-col gap-2 md:grid md:grid-cols-12 md:gap-x-6 md:items-center"
              >
                <div className="flex items-center justify-between md:block md:col-span-6">
                  <span className="md:hidden text-xs text-gray-500">Groupe</span>
                  <span>{g.label}</span>
                </div>
                <div className="flex items-center justify-between md:block md:col-span-2">
                  <span className="md:hidden text-xs text-gray-500">Quantité</span>
                  {edit ? (
                    <input
                      type="number"
                      className="w-24 h-9 text-center rounded-[10px] border px-2 bg-white"
                      style={{ borderColor: "#D1E2DD" }}
                      value={g.qty}
                      onChange={(e) => {
                        const v = Number(e.target.value || "0");
                        setGroups((prev) => prev.map((pg) => (pg.key === g.key ? { ...pg, qty: v } : pg)));
                        // Distribuer également la quantité entre les poches du groupe.
                        setBagsMap((prev) => {
                          const bags = prev[g.key] || [];
                          if (bags.length === 0) return prev;
                          const perBag = v / bags.length;
                          return {
                            ...prev,
                            [g.key]: bags.map((b) => ({ ...b, volume: perBag })),
                          };
                        });
                      }}
                    />
                  ) : (
                    <>
                      <span className="hidden md:inline text-[#0B1F16]">{g.qty}</span>
                      <span className="md:hidden inline-flex items-center justify-center h-9 px-3 rounded-[10px] border bg-white" style={{ borderColor: "#D1E2DD" }}>
                        {g.qty}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex justify-start md:justify-end gap-2 whitespace-nowrap md:col-span-4">
                  <button onClick={() => setOpenKey(g.key)} className="h-9 px-3 rounded-[10px] border bg-white shrink-0" style={{ borderColor: "#D1E2DD", color: "#00553E" }}>
                    Informations
                  </button>
                  {edit && (
                    <button onClick={() => removeGroup(g.key)} className="h-9 px-3 rounded-[10px] border text-red-600 shrink-0" style={{ borderColor: "#F3C0C0" }}>
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {openKey && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4" onClick={() => setOpenKey(null)}>
          <div
            className="w-[900px] max-w-[95vw] max-h-[85vh] bg-[#F3F3F2] rounded-[10px] border shadow flex flex-col"
            style={{ borderColor: "#D1E2DD" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 bg-[#444] text-white rounded-t-[10px]">
              <div className="flex-1">{openKey}</div>
              <div className="w-32 text-center">
                {edit ? (
                  <input
                    type="number"
                    className="w-24 h-8 rounded bg-white/90 text-black px-2"
                    value={(bagsMap[openKey!] || []).reduce((sum, b) => sum + (Number(b.volume) || 0), 0)}
                    onChange={(e) => {
                      const v = Number(e.target.value || "0");
                      const bags = bagsMap[openKey!] || [];
                      if (bags.length === 0) return;
                      const perBag = v / bags.length;
                      setGroups((prev) => prev.map((pg) => (pg.key === openKey ? { ...pg, qty: v } : pg)));
                      setBagsMap((prev) => {
                        const arr = [...(prev[openKey!] || [])];
                        return { ...prev, [openKey!]: arr.map((b) => ({ ...b, volume: perBag })) };
                      });
                    }}
                  />
                ) : (
                  <span>{(bagsMap[openKey!] || []).reduce((sum, b) => sum + (Number(b.volume) || 0), 0)}</span>
                )}
              </div>
              <div className="w-32 text-right">Informations</div>
            </div>
            <div className="h-2 bg-[#333]" />
            <div className="p-6 bg-white rounded-b-[10px] overflow-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left">
                      <th className="p-3">Sac</th>
                      <th className="p-3">Rhésus</th>
                      <th className="p-3">volume</th>
                      <th className="p-3">Date de collecte</th>
                      <th className="p-3">Date de péremption.</th>
                      <th className="p-3">Température</th>
                      <th className="p-3">Localisation</th>
                      {edit && <th className="p-3 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(bagsMap[openKey!] || []).map((b, i) => (
                      <tr key={i} className="odd:bg-gray-200">
                        <td className="p-3">
                          {b.sac}
                        </td>
                        <td className="p-3">
                          {b.rhesus}
                        </td>
                        <td className="p-3">
                          {edit ? (
                            <input
                              type="number"
                              className="h-8 w-24 rounded border px-2"
                              value={b.volume}
                              onChange={(e) => {
                                const v = Number(e.target.value || "0");
                                setBagsMap((prev) => {
                                  const arr = [...(prev[openKey!] || [])];
                                  arr[i] = { ...arr[i], volume: v };
                                  const next = { ...prev, [openKey!]: arr };
                                  // Maintenir la somme groupe/vol à jour côté UI
                                  setGroups((prevGroups) =>
                                    prevGroups.map((pg) => (pg.key === openKey ? { ...pg, qty: arr.reduce((s, bb) => s + (Number(bb.volume) || 0), 0) } : pg))
                                  );
                                  return next;
                                });
                              }}
                            />
                          ) : (
                            b.volume
                          )}
                        </td>
                        <td className="p-3">
                          {b.collecte}
                        </td>
                        <td className="p-3">
                          {b.peremption}
                        </td>
                        <td className="p-3">
                          {edit ? (
                            <input
                              type="number"
                              className="h-8 w-24 rounded border px-2"
                              value={b.temperature}
                              onChange={(e) => {
                                const v = Number(e.target.value || "0");
                                setBagsMap((prev) => {
                                  const arr = [...(prev[openKey!] || [])];
                                  arr[i] = { ...arr[i], temperature: v };
                                  return { ...prev, [openKey!]: arr };
                                });
                              }}
                            />
                          ) : (
                            b.temperature
                          )}
                        </td>
                        <td className="p-3">
                          {edit ? (
                            <input
                              className="h-8 w-36 rounded border px-2"
                              value={b.localisation}
                              onChange={(e) => {
                                const v = e.target.value;
                                setBagsMap((prev) => {
                                  const arr = [...(prev[openKey!] || [])];
                                  arr[i] = { ...arr[i], localisation: v };
                                  return { ...prev, [openKey!]: arr };
                                });
                              }}
                            />
                          ) : (
                            b.localisation
                          )}
                        </td>
                        {edit && (
                          <td className="p-3 text-right">
                            <button
                              className="h-8 px-3 rounded-[10px] border text-red-600"
                              style={{ borderColor: "#F3C0C0" }}
                              onClick={async () => {
                                if (!b.pocketId) return;
                                setSaving(true);
                                const res = await deleteBanqueSang(b.pocketId);
                                setSaving(false);
                                if (!res.ok) {
                                  setError(res.message || "Erreur lors de la suppression de la poche.");
                                  return;
                                }
                                await loadStocks();
                              }}
                            >
                              Supprimer
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!edit && (
                <div className="mt-6 flex justify-end">
                  <button
                    className="h-10 px-4 rounded-[10px] font-medium"
                    style={{ backgroundColor: "#00553E", color: "#fff" }}
                    onClick={() => {
                      setDemandForm({ quantite: 450, urgence: "faible", motif: "" });
                      setDemandOpen(true);
                    }}
                    disabled={saving}
                  >
                    Demander du sang
                  </button>
                </div>
              )}
              {edit && (
                <div className="mt-6 flex justify-end">
                  <div className="flex gap-2">
                    <button
                      className="h-10 px-4 rounded-[10px] border inline-flex items-center gap-2"
                      style={{ borderColor: "#D1E2DD" }}
                      onClick={async () => {
                        if (!openKey) return;
                        const parsed = groupLabelToBackendGroupAndRhesus(openKey);
                        if (!parsed) {
                          setError("Groupe sanguin invalide.");
                          return;
                        }
                        if (!establishment?.email) {
                          setError("Créateur (email établissement) introuvable.");
                          return;
                        }

                        const bags = bagsMap[openKey] || [];
                        const groupTotal = bags.reduce((sum, b) => sum + (Number(b.volume) || 0), 0);
                        const nextCount = bags.length + 1;
                        const volume = nextCount > 0 ? Math.max(0, Math.round(groupTotal / nextCount || 450)) : 450;

                        const now = new Date();
                        const dateCollecte = now.toISOString();
                        const datePeremption = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000).toISOString();

                        const payload = {
                          ...parsed,
                          volume,
                          dateCollecte,
                          datePeremption,
                          lot: `LOT_${openKey}_${Date.now()}`,
                          temperature: 0,
                          localisation: "",
                          createur: establishment.email,
                        };

                        setSaving(true);
                        const res = await createBanqueSang(payload);
                        setSaving(false);
                        if (!res.ok) {
                          setError(res.message || "Erreur lors de la création de la poche.");
                          return;
                        }
                        await loadStocks();
                      }}
                    >
                      <Plus size={16} /> Ajouter un sac
                    </button>
                    <button
                      className="h-10 px-4 rounded-[10px] font-medium"
                      style={{ backgroundColor: "#00553E", color: "#fff" }}
                      onClick={async () => {
                        if (!banqueId) return;
                        setSaving(true);
                        // PUT /banques-sang/:id (au minimum température, via updateBanqueSang)
                        const allBags = Object.values(bagsMap).flat();
                        for (const b of allBags) {
                          if (!b.pocketId) continue;
                          const resPoche = await updateBanqueSang(b.pocketId, {
                            temperature: b.temperature,
                            localisation: b.localisation,
                          });
                          if (!resPoche.ok) {
                            setSaving(false);
                            setError(resPoche.message || "Erreur lors de la mise à jour de la poche.");
                            return;
                          }
                        }

                        // PUT stocks : `{ stocks: { "A+": 120, ... } }`
                        const summary: Record<string, number> = Object.fromEntries(
                          Object.entries(bagsMap).map(([label, bags]) => [
                            label,
                            (bags || []).reduce((sum, bb) => sum + (Number(bb.volume) || 0), 0),
                          ])
                        );
                        const res = await updateStocks(banqueId, summary);
                        setSaving(false);
                        if (res.ok) {
                          setEdit(false);
                          await loadStocks();
                          setOpenKey(null);
                        } else setError(res.message || "Erreur lors de l'enregistrement.");
                      }}
                      disabled={!banqueId || saving}
                    >
                      {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {demandOpen && openKey && (
        <div
          className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4"
          onClick={() => setDemandOpen(false)}
        >
          <div
            className="w-[520px] max-w-[95vw] bg-white rounded-[10px] border shadow p-6 space-y-4"
            style={{ borderColor: "#E5E7EB" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">Demander du sang ({openKey})</h3>

            <div className="space-y-3">
              <div className="space-y-2">
                <label className="text-sm">Quantité (ml)</label>
                <input
                  type="number"
                  className="w-full h-10 rounded-[10px] border px-3"
                  style={{ borderColor: "#D1E2DD" }}
                  value={demandForm.quantite}
                  onChange={(e) => setDemandForm((p) => ({ ...p, quantite: Number(e.target.value || "0") }))}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm">Urgence</label>
                <select
                  className="w-full h-10 rounded-[10px] border px-3"
                  style={{ borderColor: "#D1E2DD" }}
                  value={demandForm.urgence}
                  onChange={(e) => setDemandForm((p) => ({ ...p, urgence: e.target.value as DemandeUrgence }))}
                >
                  <option value="faible">faible</option>
                  <option value="moyenne">moyenne</option>
                  <option value="haute">haute</option>
                  <option value="critique">critique</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm">Motif</label>
                <textarea
                  className="w-full min-h-[90px] rounded-[10px] border px-3 pt-2"
                  style={{ borderColor: "#D1E2DD" }}
                  value={demandForm.motif}
                  onChange={(e) => setDemandForm((p) => ({ ...p, motif: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="h-10 px-4 rounded-[10px] border"
                style={{ borderColor: "#D1E2DD" }}
                onClick={() => setDemandOpen(false)}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                className="h-10 px-4 rounded-[10px] text-white"
                style={{ backgroundColor: "#00553E" }}
                onClick={async () => {
                  if (!openKey) return;
                  if (!demandForm.motif.trim()) {
                    setError("Motif requis.");
                    return;
                  }
                  if (!Number.isFinite(demandForm.quantite) || demandForm.quantite <= 0) {
                    setError("Quantité invalide.");
                    return;
                  }
                  setSaving(true);
                  const res = await demandSang({
                    groupeSanguin: openKey as any,
                    quantite: demandForm.quantite,
                    urgence: demandForm.urgence,
                    motif: demandForm.motif,
                  });
                  setSaving(false);
                  if (!res.ok) {
                    setError(res.message || "Erreur lors de la demande.");
                    return;
                  }
                  setDemandOpen(false);
                  await loadStocks();
                }}
                disabled={saving}
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}

      {addingGroup && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4" onClick={() => setAddingGroup(false)}>
          <div
            className="w-[520px] max-w-[95vw] bg-white rounded-[10px] border shadow p-6 space-y-4"
            style={{ borderColor: "#E5E7EB" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">Ajouter un groupe sanguin</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm">Libellé (ex: AB+)</label>
                <input
                  className="w-full h-10 rounded-[10px] border px-3"
                  style={{ borderColor: "#D1E2DD" }}
                  value={newGroup.label}
                  onChange={(e) => setNewGroup({ ...newGroup, label: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm">Quantité</label>
                <input
                  type="number"
                  className="w-full h-10 rounded-[10px] border px-3"
                  style={{ borderColor: "#D1E2DD" }}
                  value={newGroup.qty}
                  onChange={(e) => setNewGroup({ ...newGroup, qty: parseInt(e.target.value || "0", 10) })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button className="h-10 px-4 rounded-[10px] border" style={{ borderColor: "#D1E2DD" }} onClick={() => setAddingGroup(false)}>
                Annuler
              </button>
              <button className="h-10 px-4 rounded-[10px] text-white" style={{ backgroundColor: "#00553E" }} onClick={addGroup}>
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-sm text-gray-600">
        Mettre à jour le maps permettra de visualiser en temps réel la disponibilité des lits dans les hôpitaux, des poches de sang par groupe sanguin et
        localité, des médicaments et matériels dans les hôpitaux et pharmacies publiques.
      </p>
    </div>
  );
}
